import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  ClipboardCheck,
  Info,
  Loader2,
  MessageSquare,
  Send,
  Star,
  TrendingUp,
  UserCheck,
  Users,
  Video,
  Wallet,
} from 'lucide-react';
import { POSITIVE_REPLY, STEPS, earningsLine } from '@/lib/outreach-copy';
import { creatorTypeLabel, payModelLabel, rateTypeLabel, titleCase } from '@/lib/labels';
import { useApi } from '@/hooks/useApi';
import { useMe } from '@/hooks/useMe';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RetainerCard } from '@/components/RetainerCard';
import { formatMoney } from '@/lib/money';
import type {
  CompensationModel,
  OutreachFollowerBracket,
  OutreachNiche,
  OutreachRate,
} from '@/api/types';

// ── Step definitions ───────────────────────────────────────────────────────
// Only the rate types the payout calculator actually pays: LOG_BONUS,
// JOIN_BONUS and POST_BASE (each with optional niche-scoped rows added on
// top). NICHE_BONUS and INFLUENCER_FLAT rows can exist in the rate table but
// the backend never pays them, so this page never shows them as money.
// Which steps render is driven by the rate data; nothing is hardcoded per
// creator type.

type PaidRateType = 'LOG_BONUS' | 'JOIN_BONUS' | 'POST_BASE';

interface StepDef {
  rateType: PaidRateType;
  icon: React.ReactNode;
  color: StepColor;
  when: string;
  description: string;
}

const ALL_STEPS: StepDef[] = [
  {
    rateType: 'LOG_BONUS',
    icon: <ClipboardCheck className="h-4.5 w-4.5" />,
    color: 'blue',
    when: 'Earned when you log a creator who replied asking to join; paid in the next payout',
    description:
      "The screenshot has to show the creator's reply asking how to join. It doesn't depend on them going on to join or post. Marking someone messaged never earns this. On Discord invites it's earned once the creator is confirmed in the server.",
  },
  {
    rateType: 'JOIN_BONUS',
    icon: <UserCheck className="h-4.5 w-4.5" />,
    color: 'green',
    when: 'When they join the clipping platform (or Discord server) after you logged them',
    description:
      'Joins are confirmed automatically with clipping, or by an admin if your team uses manual tracking. It only counts for creators you logged.',
  },
  {
    rateType: 'POST_BASE',
    icon: <Video className="h-4.5 w-4.5" />,
    color: 'purple',
    when: 'Per approved post made after you logged them, up to the cap',
    // The cap is per creator type and set by admins (POST_BASE metadata, 5 by
    // default), and the portal rate list doesn't expose it, so no number here.
    description:
      "Each approved campaign post by a creator you logged earns this, up to a set number of posts per creator. Posts that aren't approved don't count.",
  },
];

// ── Creator-type copy ──────────────────────────────────────────────────────
// Subtitle/explanation text varies per creator type. Which steps actually
// render is driven entirely by the rate data, NOT by this config.

interface CategoryCopy {
  subtitle: string;
  howItWorks: string;
  stackingNote: string;
}

const CATEGORY_COPY: Record<string, CategoryCopy> = {
  CLIPPER: {
    subtitle: 'Message freely, log the creators who reply asking to join, and earn at the stages below.',
    howItWorks:
      'Messaging is unpaid and unlimited: DM as many creators as you like and tap "I messaged them". When one replies asking how to join, log the reply with a screenshot of it. Logging links the creator to you, so each stage they reach afterwards (joining, approved posts, the follower bonus) is credited to you.',
    stackingNote:
      'Amounts stack across stages. Which stages pay, and how much, is the rate card above: an invite may earn when you log it, or only once the creator joins and posts.',
  },
  MICRO_UGC: {
    subtitle: 'You earn when creators you logged join the clipping platform and post approved content.',
    howItWorks:
      'Your earnings follow results. Messaging is unpaid: DM as many creators as you like and tap "I messaged them". Log the reply once a creator answers asking how to join, with that reply in the screenshot. Logging may pay nothing on its own here; it links the creator to you, so the join amount lands when they join and the post amount lands for each approved post.',
    stackingNote:
      'Join and post amounts stack. For one creator you earn the join amount when they join, then the post amount for each approved post, up to the cap.',
  },
  INFLUENCER: {
    subtitle: 'What each influencer invite earns, from your rate card.',
    howItWorks:
      'Mark influencers messaged when you DM them. Log the reply only once the influencer has answered asking how to join, with that reply in the screenshot. What the invite earns is the amount listed below.',
    stackingNote: 'Influencer invites earn the amounts listed above, nothing more.',
  },
};

const DEFAULT_COPY: CategoryCopy = {
  subtitle: 'What each invite earns, stage by stage.',
  howItWorks:
    'Messaging is unpaid. Logging a creator who replied asking to join links them to you, and the amounts below are credited as they reach each stage.',
  stackingNote: 'Amounts stack across stages on a single invite.',
};

// ── Styling ────────────────────────────────────────────────────────────────

const ICON_CIRCLE =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold';

const STEP_COLORS = {
  blue: {
    bg: 'bg-blue-500/15',
    text: 'text-blue-600',
    border: 'border-blue-500/20',
    surface: 'bg-blue-500/[0.04]',
    pill: 'bg-blue-500/10 text-blue-700 border-blue-500/20',
  },
  green: {
    bg: 'bg-emerald-500/15',
    text: 'text-emerald-600',
    border: 'border-emerald-500/20',
    surface: 'bg-emerald-500/[0.04]',
    pill: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20',
  },
  purple: {
    bg: 'bg-purple-500/15',
    text: 'text-purple-600',
    border: 'border-purple-500/20',
    surface: 'bg-purple-500/[0.04]',
    pill: 'bg-purple-500/10 text-purple-700 border-purple-500/20',
  },
  amber: {
    bg: 'bg-amber-500/15',
    text: 'text-amber-600',
    border: 'border-amber-500/20',
    surface: 'bg-amber-500/[0.04]',
    pill: 'bg-amber-500/10 text-amber-700 border-amber-500/20',
  },
} as const;

type StepColor = keyof typeof STEP_COLORS;

// ── Rate shapes ────────────────────────────────────────────────────────────

interface NicheRate {
  niche: string;
  nicheName: string;
  amount: string;
  isFocused: boolean;
  /** Hidden niche: can't be tagged on new invites, but still pays on old ones. */
  isHidden: boolean;
}

/** One creator type's rates for one stage. */
interface CategoryRates {
  category: string;
  /** Base amount for every invite of this type. Null when no base row exists. */
  general: string | null;
  /** Niche-scoped amounts, added on top of the base for the first niche tagged. */
  nicheRates: NicheRate[];
}

const isPaidRateType = (t: string): t is PaidRateType =>
  t === 'LOG_BONUS' || t === 'JOIN_BONUS' || t === 'POST_BASE';

// ── Component ──────────────────────────────────────────────────────────────

export function RatesPage(): JSX.Element {
  const api = useApi();
  const me = useMe();
  const rates = useQuery({
    queryKey: ['portal-rates'],
    queryFn: () => api.get<OutreachRate[]>('/portal/rates'),
  });
  const brackets = useQuery({
    queryKey: ['portal-brackets'],
    queryFn: () => api.get<OutreachFollowerBracket[]>('/portal/follower-brackets'),
  });
  const niches = useQuery({
    queryKey: ['portal-niches'],
    // Hidden niches too: their bonuses still pay on invites already tagged.
    queryFn: () => api.get<OutreachNiche[]>('/portal/niches', { includeHidden: true }),
  });

  const nicheMap = useMemo(() => {
    const map = new Map<string, OutreachNiche>();
    for (const n of niches.data ?? []) map.set(n.code, n);
    return map;
  }, [niches.data]);

  // rateType → creator type → rates. Keyed by creator type so a team with
  // more than one type doesn't have one type's amount overwrite another's.
  const ratesByType = useMemo(() => {
    const map = new Map<PaidRateType, Map<string, CategoryRates>>();
    for (const r of rates.data ?? []) {
      if (!r.isActive || !isPaidRateType(r.rateType)) continue;
      const byCat = map.get(r.rateType) ?? new Map<string, CategoryRates>();
      const entry = byCat.get(r.category) ?? { category: r.category, general: null, nicheRates: [] };
      if (r.niche === null) {
        entry.general = r.amount;
      } else {
        const info = nicheMap.get(r.niche);
        entry.nicheRates.push({
          niche: r.niche,
          nicheName: info?.displayName ?? titleCase(r.niche),
          amount: r.amount,
          isFocused: info?.isFocused ?? false,
          isHidden: info?.isHidden ?? false,
        });
      }
      byCat.set(r.category, entry);
      map.set(r.rateType, byCat);
    }
    for (const byCat of map.values()) {
      for (const entry of byCat.values()) {
        entry.nicheRates.sort((a, b) => {
          const da = parseFloat(a.amount);
          const db = parseFloat(b.amount);
          if (da !== db) return db - da;
          if (a.isFocused !== b.isFocused) return a.isFocused ? -1 : 1;
          return a.nicheName.localeCompare(b.nicheName);
        });
      }
    }
    return map;
  }, [rates.data, nicheMap]);

  const sortedBrackets = useMemo(() => {
    const arr = (brackets.data ?? []).filter((b) => b.isActive);
    arr.sort((a, b) => a.category.localeCompare(b.category) || a.minFollowers - b.minFollowers);
    return arr;
  }, [brackets.data]);

  // The outreacher's creator types (team's allowed categories).
  const categories =
    me.data?.role === 'outreacher' ? (me.data.team?.allowedCategories ?? []) : [];
  const primaryCategory = categories[0] ?? null;
  const singleType = categories.length === 1;
  const copy = (singleType && primaryCategory && CATEGORY_COPY[primaryCategory]) || DEFAULT_COPY;
  const heading = singleType && primaryCategory ? `${creatorTypeLabel(primaryCategory)} rates` : 'Your rates';

  const inviter = me.data?.role === 'outreacher' ? me.data.inviter : null;
  const isLoading = rates.isLoading || brackets.isLoading || niches.isLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <RatesHero heading={heading} copy={copy} />
        <Loading />
      </div>
    );
  }

  // Only show steps that have active rates in the data
  const activeSteps = ALL_STEPS.filter((s) => ratesByType.has(s.rateType));

  // Rate rows seen across creator types, so labels name the type when there
  // is more than one.
  const typesInRates = new Set<string>();
  for (const byCat of ratesByType.values()) for (const c of byCat.keys()) typesInRates.add(c);
  const showTypeLabels = typesInRates.size > 1;

  // What this outreacher earns the moment an invite is logged: the base
  // LOG_BONUS for their (first) creator type. Null when no log row exists
  // (can't claim anything either way); 0 is meaningful and means "nothing
  // at log time", which changes the wording entirely.
  const logBonusTotal = (() => {
    const log = ratesByType.get('LOG_BONUS');
    if (!log) return null;
    const entry = (primaryCategory && log.get(primaryCategory)) || log.values().next().value;
    if (!entry || entry.general === null) return null;
    const n = Number.parseFloat(entry.general);
    return Number.isFinite(n) ? n : 0;
  })();
  const hasBrackets = sortedBrackets.length > 0;
  const hasNicheRates = Array.from(ratesByType.values()).some((byCat) =>
    Array.from(byCat.values()).some((e) => e.nicheRates.length > 0),
  );

  // Retainer pay replaces every per-invite amount, so there is no rate card
  // to show them.
  if (inviter?.compensationModel === 'RETAINER') {
    return (
      <div className="space-y-8">
        <RatesHero heading={heading} copy={copy} />
        <RetainerCard retainerAmount={inviter.retainerAmount} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <RatesHero heading={heading} copy={copy} />

      {inviter && (
        <PayModelNote model={inviter.compensationModel} retainerAmount={inviter.retainerAmount} />
      )}

      {/* ── How it works ── */}
      <Card className="border border-border/60">
        <CardHeader className="pb-1">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            How you earn
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* The two actions, side by side. Outreachers were conflating
              "messaged" with "logged"; showing them as separate cards with
              their own bar makes the difference structural rather than a
              sentence someone has to read carefully. */}
          <div className="mb-4 grid gap-3 sm:grid-cols-2">
            <StepCard
              step={1}
              icon={<Send className="h-4 w-4" />}
              verb={STEPS.messaged.verb}
              when={STEPS.messaged.when}
              means={STEPS.messaged.means}
              tone="muted"
            />
            <StepCard
              step={2}
              icon={<MessageSquare className="h-4 w-4" />}
              verb={STEPS.logged.verb}
              when={STEPS.logged.when}
              means={STEPS.logged.means}
              tone="primary"
            />
          </div>

          {/* Says what THIS team's card actually pays, rather than assuming a
              log bonus exists. Several teams run a $0 log bonus and earn only
              on joins/posts. */}
          <p className="mb-4 text-sm text-foreground/80">{earningsLine(logBonusTotal)}</p>

          <div className="mb-4 rounded-lg border border-warning/40 bg-warning/[0.08] px-4 py-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
              <div className="space-y-1">
                <p className="text-sm font-bold text-foreground">{POSITIVE_REPLY.headline}</p>
                <p className="text-sm text-foreground/80">{POSITIVE_REPLY.qualifies}</p>
                <p className="text-sm text-foreground/80">{POSITIVE_REPLY.doesNot}</p>
              </div>
            </div>
          </div>

          <p className="mb-4 text-sm text-foreground/80">{copy.howItWorks}</p>
          <div className="flex flex-col gap-0">
            {activeSteps.map((step, i) => {
              const c = STEP_COLORS[step.color];
              return (
                <div key={step.rateType} className="flex items-start gap-3 py-2.5">
                  <div className={`${ICON_CIRCLE} ${c.bg} ${c.text}`}>{i + 1}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={c.text}>{step.icon}</span>
                      <span className="text-sm font-semibold">{rateTypeLabel(step.rateType)}</span>
                    </div>
                    <p className="mt-0.5 text-sm text-muted-foreground">{step.when}</p>
                  </div>
                </div>
              );
            })}
            {hasBrackets && (
              <div className="flex items-start gap-3 py-2.5">
                <div className={`${ICON_CIRCLE} bg-orange-500/15 text-orange-600`}>
                  <TrendingUp className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-orange-600">
                      <Users className="h-4 w-4" />
                    </span>
                    <span className="text-sm font-semibold">Follower bonus</span>
                  </div>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    Extra, once, if a creator you logged has an approved post and enough followers
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── Rate sections for each step ── */}
      {activeSteps.map((step) => {
        const byCat = ratesByType.get(step.rateType);
        if (!byCat) return null;
        return (
          <RateSection
            key={step.rateType}
            step={step}
            rates={Array.from(byCat.values())}
            showTypeLabels={showTypeLabels}
          />
        );
      })}

      {/* ── Follower bonus ── */}
      {hasBrackets && (
        <section className="space-y-4">
          <div className="flex items-start gap-3">
            <div className={`${ICON_CIRCLE} bg-orange-500/15 text-orange-600`}>
              <TrendingUp className="h-4.5 w-4.5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Follower bonus</h2>
              <p className="text-sm text-muted-foreground">
                Extra based on the creator's follower count
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-orange-500/20 bg-orange-500/[0.04] px-4 py-3">
            <div className="flex items-start gap-2">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-orange-600" />
              <p className="text-sm text-foreground/80">
                The follower bonus <span className="font-semibold">only applies once the creator
                has an approved post</span>. If a creator joins but never posts, no follower bonus
                is earned. Only the highest bracket they reach counts.
              </p>
            </div>
          </div>

          <Card className="border border-orange-500/20">
            <CardContent className="space-y-1 pt-4">
              {sortedBrackets.map((b, i) => {
                const next = sortedBrackets[i + 1];
                const nextMin = next && next.category === b.category ? next.minFollowers : undefined;
                const rangeLabel = nextMin
                  ? `${fmtFollowers(b.minFollowers)} to ${fmtFollowers(nextMin - 1)}`
                  : `${fmtFollowers(b.minFollowers)}+`;
                return (
                  <div
                    key={b.id}
                    className="flex items-center justify-between gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-accent/40"
                  >
                    <div className="flex items-center gap-2.5 text-sm">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span>
                        {showTypeLabels && (
                          <span className="font-medium">{creatorTypeLabel(b.category)}: </span>
                        )}
                        {rangeLabel} followers
                      </span>
                    </div>
                    <span className="text-lg font-semibold tabular-nums text-orange-600">
                      +${formatMoney(b.rate)}
                    </span>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </section>
      )}

      {/* ── Bottom note ── */}
      <Card className="border border-dashed border-border bg-muted/30">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="space-y-1.5 text-sm text-muted-foreground">
              <p>{copy.stackingNote}</p>
              {hasNicheRates && (
                <p>
                  Niche bonuses are added on top of the base amount for the{' '}
                  <span className="font-medium text-foreground">first niche</span> you tag on
                  an invite. If you tag more than one, the rest are recorded for context but
                  don't add to your pay.
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Subcomponents ──────────────────────────────────────────────────────────

/**
 * One of the two actions an outreacher takes, as a card. Deliberately shows
 * the trigger ("when") above the consequence ("means"); the confusion being
 * fixed is people logging at DM time, which is a when-question.
 */
function StepCard({
  step,
  icon,
  verb,
  when,
  means,
  tone,
}: {
  step: number;
  icon: React.ReactNode;
  verb: string;
  when: string;
  means: string;
  tone: 'muted' | 'primary';
}): JSX.Element {
  const isPrimary = tone === 'primary';
  return (
    <div
      className={`rounded-lg border px-4 py-3 ${
        isPrimary ? 'border-brand/40 bg-brand/[0.06]' : 'border-border/60 bg-muted/30'
      }`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
            isPrimary ? 'bg-brand/20 text-brand' : 'bg-muted-foreground/15 text-muted-foreground'
          }`}
        >
          {step}
        </span>
        <span className={isPrimary ? 'text-primary' : 'text-muted-foreground'}>{icon}</span>
        <span className="text-sm font-semibold">{verb}</span>
      </div>
      <p className="mt-1.5 text-sm font-medium text-foreground">{when}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{means}</p>
    </div>
  );
}

function RatesHero({ heading, copy }: { heading: string; copy: CategoryCopy }): JSX.Element {
  return (
    <div>
      <h1 className="text-2xl font-semibold">{heading}</h1>
      <p className="max-w-[65ch] text-sm text-muted-foreground">{copy.subtitle}</p>
    </div>
  );
}

/**
 * How this outreacher is paid. Retainer outreachers are paid the retainer
 * instead of per-invite amounts (payroll skips their invite line items), so
 * the rate card below is context, not money owed to them.
 */
function PayModelNote({
  model,
  retainerAmount,
}: {
  model: CompensationModel;
  retainerAmount: string | null;
}): JSX.Element {
  const isRetainer = model === 'RETAINER';
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border/60 bg-muted/30 px-4 py-3">
      <Wallet className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="text-sm">
        <p className="font-semibold text-foreground">
          Pay model: {payModelLabel(model)}
          {isRetainer && retainerAmount ? ` ($${formatMoney(retainerAmount)} a week)` : ''}
        </p>
        <p className="mt-0.5 text-muted-foreground">
          {isRetainer
            ? "You're paid your retainer instead of the per-invite amounts below. They're shown so you can see how invites are valued."
            : 'Each invite you log earns the amounts below as the creator reaches each stage.'}
        </p>
      </div>
    </div>
  );
}

function RateSection({
  step,
  rates,
  showTypeLabels,
}: {
  step: StepDef;
  rates: CategoryRates[];
  showTypeLabels: boolean;
}): JSX.Element {
  const c = STEP_COLORS[step.color];
  return (
    <section className="space-y-4">
      <div className="flex items-start gap-3">
        <div className={`${ICON_CIRCLE} ${c.bg} ${c.text}`}>{step.icon}</div>
        <div>
          <h2 className="text-lg font-semibold">{rateTypeLabel(step.rateType)}</h2>
          <p className="text-sm text-muted-foreground">{step.when}</p>
        </div>
      </div>

      <div className={`rounded-lg border ${c.border} ${c.surface} px-4 py-3`}>
        <p className="text-sm text-foreground/80">{step.description}</p>
      </div>

      <Card className={`border ${c.border}`}>
        <CardContent className="space-y-4 pt-4">
          {rates.map((entry) => (
            <div key={entry.category} className="space-y-2">
              {showTypeLabels && (
                <p className="px-1 text-xs font-semibold text-foreground">
                  {creatorTypeLabel(entry.category)}
                </p>
              )}
              {entry.general !== null && (
                <div className="flex items-center justify-between gap-3 rounded-md bg-muted/40 px-4 py-3">
                  <span className="text-sm font-medium text-muted-foreground">Every invite</span>
                  <span className={`text-xl font-semibold tabular-nums ${c.text}`}>
                    ${formatMoney(entry.general)}
                  </span>
                </div>
              )}
              {entry.nicheRates.length > 0 && (
                <div className="space-y-1">
                  <p className="px-1 pt-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Niche bonus, added on top
                  </p>
                  {entry.nicheRates.map((nr) => (
                    <div
                      key={nr.niche}
                      className="flex items-center justify-between gap-3 rounded-md px-4 py-2 transition-colors hover:bg-accent/40"
                    >
                      <span className="flex items-center gap-1.5 text-sm">
                        {nr.nicheName}
                        {nr.isFocused && (
                          <span
                            className={`inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold leading-none ${c.pill}`}
                          >
                            <Star className="h-2.5 w-2.5 fill-current" />
                            Focus
                          </span>
                        )}
                        {nr.isHidden && (
                          <span
                            className="text-[11px] text-muted-foreground"
                            title="You can't tag new invites with this niche. Invites already tagged with it still earn the bonus."
                          >
                            No longer offered
                          </span>
                        )}
                      </span>
                      <span className={`font-semibold tabular-nums ${c.text}`}>
                        +${formatMoney(nr.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </section>
  );
}

// ── Utilities ──────────────────────────────────────────────────────────────

function fmtFollowers(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toLocaleString();
}

function Loading(): JSX.Element {
  return (
    <div className="flex items-center text-muted-foreground">
      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading
    </div>
  );
}
